import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { BookingPreConfirmationService } from '../service/booking-pre-confirmation.service';
import { BookingPreConfirmation } from '../booking-pre-confirmation';
import { BookingPreConfirmationTemplate } from '../booking-pre-confirmation-template';
import { BookingService } from '../service/booking.service';
import { CKEDITORCONFIGMINIMAL, CKEDITORCONFIGMINIMALWITHLINK } from '../ckeditor-config';
import Decimal from 'decimal.js';
import { HttpService } from '../service/http.service';
import { LviBankInformation } from '../lvi-bank-information';
import { SalesProduct } from '../sales-product';
import { ToastMessageService } from '../toast-message.service';
import * as moment from 'moment';
import { BookingPreconfirmationTotals } from '../booking-preconfirmation-totals';
import { ExchangeRateService } from '../service/exchange-rate.service';

@Component({
  selector: 'app-create-booking-pre-confirmation',
  templateUrl: './create-booking-pre-confirmation.component.html',
  styleUrls: ['./create-booking-pre-confirmation.component.css']
})
export class CreateBookingPreConfirmationComponent implements OnInit {

  editorConfig = CKEDITORCONFIGMINIMALWITHLINK;
  editorConfigMin = CKEDITORCONFIGMINIMAL;
  template!: BookingPreConfirmationTemplate;
  bankInformations: LviBankInformation[] = [];
  products: SalesProduct[] = [];
  balance: Decimal = new Decimal(0.00);
  totals: BookingPreconfirmationTotals[] = [];
  isConfirmed: boolean = false;
  reloadingTemplate = true;
  preConfirmationDate: moment.Moment | null = null;
  DEFAULT_DEPOSIT_AMOUNT = 0;
  useLegacy = false;

  @Output() preConfirmationCreatedEvent = new EventEmitter<boolean>();

  constructor(
    private httpService: HttpService,
    public bookingService: BookingService,
    private toastService: ToastMessageService,
    private bookingConfirmationService: BookingPreConfirmationService,
    private exchangeRateService: ExchangeRateService
  ) { }

  ngOnInit(): void {
    this.httpService.getAllLviBankInformations().subscribe(res => {
      let preConfirmationDate = null;
      if(this.bookingService.booking.preConfirmation!.confirmationDate == null){
        preConfirmationDate = moment().utc(true);
      } else {
        preConfirmationDate = moment(this.bookingService.booking.preConfirmation!.confirmationDate).utc(true);
      }
      this.preConfirmationDate = preConfirmationDate;
      this.bankInformations = res;
      this.products = this.bookingService.booking.preConfirmation!.productList;
      this.setConvertedAmountToProducts();
      this.calculateTotal();
      this.httpService.getCompleteBookingPreConfirmationTemplateByLanguage(this.bookingService.booking.student.language.id, this.bookingService.booking.id!, this.isConfirmed).subscribe(res => {
        this.template = res;
        this.reloadingTemplate = false;
      }, error => {
        this.toastService.addError("Error", error.message);
        this.reloadingTemplate = false;
      });
    });
  }

  setConvertedAmountToProducts(){
    for(let product of this.products){
      let rate = this.exchangeRateService.calculatePriceInCurrency(new Decimal(product.amount), product.currency, this.bookingService.booking.preConfirmation!.baseCurrency,
        this.bookingService.booking.preConfirmation!.baseCurrency,  
        this.preConfirmationDate!);
      rate.subscribe(amount => {
        product.amountInBase = new Decimal(amount).ceil();
      });
      if(this.bookingService.booking.preConfirmation!.secondCurrency){
        let secondRate = this.exchangeRateService.calculatePriceInCurrency(new Decimal(product.amount), product.currency, this.bookingService.booking.preConfirmation!.secondCurrency, 
          this.bookingService.booking.preConfirmation!.baseCurrency, this.preConfirmationDate!);
        secondRate.subscribe(amount => {
          product.amountInSecond = new Decimal(amount).ceil();
        });
      }
    }
  }

  calculateTotal(){
    this.totals.push(new BookingPreconfirmationTotals(
      "Total",
      this.bookingConfirmationService.totalPriceInBaseCurrency,
      this.bookingConfirmationService.totalPriceInSecondCurrency
    ));
    this.totals.push(new BookingPreconfirmationTotals(
      "Received",
      this.bookingConfirmationService.totalReceivedInBaseCurrency,
      this.bookingConfirmationService.totalReceivedInSecondCurrency
    ));
    this.totals.push(new BookingPreconfirmationTotals(
      "Balance",
      this.bookingConfirmationService.totalPriceInBaseCurrency.minus(this.bookingConfirmationService.totalReceivedInBaseCurrency),
      this.bookingConfirmationService.totalPriceInSecondCurrency.minus(this.bookingConfirmationService.totalReceivedInSecondCurrency)
    ));
  }

  postPreConf(){
    if(!this.bookingService.booking.preConfirmation!.deposit){
      this.bookingService.booking.preConfirmation!.deposit = new Decimal(this.DEFAULT_DEPOSIT_AMOUNT);
    }
    let conf = new BookingPreConfirmation(
      this.bookingService.booking.preConfirmation!.description,
      this.bookingService.booking.preConfirmation!.comment,
      this.bookingService.booking.preConfirmation!.productList,
      this.bookingService.booking.preConfirmation!.baseCurrency,
      this.bookingService.booking.preConfirmation!.secondCurrency,
      this.bookingService.booking.preConfirmation!.confirmationDate,
      this.bookingService.booking.preConfirmation!.dueDate,
      this.bookingService.booking.preConfirmation!.deposit,
      this.bookingService.booking.preConfirmation!.priceIncludes,
      this.bookingService.booking.preConfirmation!.priceNotIncludes,
      this.template,
      this.bookingService.booking.preConfirmation!.selectedBank,
      this.bookingService.booking.preConfirmation!.middleComment,
      this.hasDepositInReal()
    );
    this.reloadingTemplate = true;
    this.httpService.postBookingPreConfirmationFile(conf, this.bookingService.booking.id!, this.bookingService.booking.student.language.id, this.isConfirmed, this.useLegacy).subscribe(() => {
      this.toastService.addSuccess("Success", "Pre confirmation posted");
      this.preConfirmationCreatedEvent.emit(true);
    }, error => {
      this.reloadingTemplate = false;
      this.toastService.addError("Error", error.message);
    });
  }

  hasDepositInReal(){
    if(this.bookingService.booking.salesAgent.id == 5){
      return true;
    }
    return false;
  }

  confirmationChange(checked: boolean){
    this.isConfirmed = checked;
    this.reloadingTemplate = true;
    this.httpService.getCompleteBookingPreConfirmationTemplateByLanguage(this.bookingService.booking.student.language.id, this.bookingService.booking.id!, this.isConfirmed).subscribe(res => {
      this.template = res;
      this.reloadingTemplate = false;
    }, error => {
      this.toastService.addError("Error", error.message);
      this.reloadingTemplate = false;
    });

  }

}
